import type {
  CreateInvoicePayload,
  ClinicInvoice,
  DuplicateInvoiceConflict,
  SaveInvoiceResult,
  UpdateInvoicePayload,
} from "@/features/clinic/types/invoice";

import { createId, createPastTimestamp } from "../../generators/base";
import { nextInvoiceCode } from "../../generators/invoice";
import { useMockStore } from "../../store";

import { deriveAppointmentValue } from "./appointment-value";

import { countsTowardValue } from "@/features/clinic/helpers/invoice";
import {
  INVOICE_ERROR_CODE,
  INVOICE_REVIEW_STATE,
  INVOICE_SOURCE,
  INVOICE_STATUS,
} from "@/features/clinic/types/invoice";

/**
 * Stands in for the server's banded check with a plain equality test - mock
 * mode only has to make the conflict reachable, not reproduce how the API
 * decides. The 409 body it returns matches shape for shape.
 */
function duplicateConflictMock(
  appointmentId: string,
  total: string,
  excludeId?: string,
): DuplicateInvoiceConflict | null {
  const duplicates = useMockStore
    .getState()
    .invoices.filter(
      (invoice) =>
        invoice.appointmentId === appointmentId &&
        invoice.id !== excludeId &&
        invoice.total === total &&
        countsTowardValue(invoice),
    );

  if (duplicates.length === 0) return null;

  return {
    code: INVOICE_ERROR_CODE.DUPLICATE_INVOICE,
    message:
      "This appointment already has an invoice for a similar amount. Somebody may have just added it.",
    duplicates,
    addAnywayAllowed: true,
  };
}

export function createInvoiceMock(
  payload: CreateInvoicePayload,
): SaveInvoiceResult {
  const store = useMockStore.getState();
  const appointment = store.appointments.find(
    (a) => a.id === payload.appointmentId,
  );
  const total = payload.total.toFixed(2);

  if (!payload.confirmDuplicate) {
    const conflict = duplicateConflictMock(payload.appointmentId, total);

    if (conflict) return { status: "duplicate", conflict };
  }

  const invoice: ClinicInvoice = {
    id: createId(),
    appointmentId: payload.appointmentId,
    contactId:
      appointment?.contactId ?? store.contacts[0]?.id ?? "mock-contact",
    code: nextInvoiceCode(store.invoices),
    externalCode: payload.externalCode ?? null,
    source: INVOICE_SOURCE.MANUAL,
    integrationId: null,
    invoiceDate: null,
    paymentType: null,
    createdByCrmUser: null,
    matchedUserId: null,
    subtotal: null,
    discount: null,
    vat: null,
    total,
    // Never asked for: a recorded invoice is money that changed hands.
    status: INVOICE_STATUS.PAID,
    reviewState: INVOICE_REVIEW_STATE.APPLIED,
    paidAt: payload.paidAt ?? createPastTimestamp(0),
    notes: payload.notes ?? null,
    createdByUserId: "mock-admin",
    organizationId: appointment?.organizationId ?? "mock-org",
    workspaceId: appointment?.workspaceId ?? "mock-workspace",
    createdAt: createPastTimestamp(0),
    updatedAt: createPastTimestamp(0),
  };

  store.addInvoice(invoice);
  deriveAppointmentValue(payload.appointmentId);

  return { status: "saved", invoice };
}

export function updateInvoiceMock(
  invoiceId: string,
  payload: UpdateInvoicePayload,
): SaveInvoiceResult | undefined {
  const store = useMockStore.getState();
  const existing = store.invoices.find((invoice) => invoice.id === invoiceId);

  if (!existing) return undefined;

  const amount = payload.total?.toFixed(2);

  if (
    amount !== undefined &&
    amount !== existing.total &&
    !payload.confirmDuplicate &&
    existing.appointmentId
  ) {
    const conflict = duplicateConflictMock(
      existing.appointmentId,
      amount,
      invoiceId,
    );

    if (conflict) return { status: "duplicate", conflict };
  }

  const updates: Partial<ClinicInvoice> = {
    ...(amount !== undefined ? { total: amount } : {}),
    ...(payload.externalCode !== undefined
      ? { externalCode: payload.externalCode || null }
      : {}),
    ...(payload.paidAt !== undefined ? { paidAt: payload.paidAt } : {}),
    ...(payload.notes !== undefined ? { notes: payload.notes || null } : {}),
    updatedAt: new Date().toISOString(),
  };

  store.updateInvoice(invoiceId, updates);
  if (existing.appointmentId) deriveAppointmentValue(existing.appointmentId);

  return { status: "saved", invoice: { ...existing, ...updates } };
}

export function deleteInvoiceMock(invoiceId: string): void {
  const store = useMockStore.getState();
  const existing = store.invoices.find((invoice) => invoice.id === invoiceId);

  if (!existing) return;

  store.deleteInvoice(invoiceId);
  if (existing.appointmentId) deriveAppointmentValue(existing.appointmentId);
}
